import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, Presentation, Globe, Calendar, MapPin, CheckCircle, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { services } from '../data/mock';

const ServicesPage = () => {
  const iconMap = {
    Building2,
    Users,
    Presentation,
    Globe,
    Calendar,
    MapPin,
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 bg-slate-900">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1610374792793-f016b77ca51a?w=1600&q=80"
            alt="Meeting Room"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 to-slate-900" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Nuestros <span className="text-sky-400">Servicios</span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Soluciones integrales de espacios corporativos diseñadas para 
              adaptarse a las necesidades únicas de cada empresa.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon] || Building2;
              return (
                <Card
                  key={service.id}
                  className="group bg-white border border-slate-100 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
                >
                  <CardContent className="p-8">
                    <div className="w-16 h-16 rounded-2xl bg-sky-100 text-sky-600 flex items-center justify-center mb-6 group-hover:bg-sky-500 group-hover:text-white transition-all duration-300">
                      <Icon className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-800 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    <ul className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-sky-500 mr-3 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              ¿Por qué elegir Corporativo MK?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Nos diferenciamos por ofrecer más que un espacio de trabajo.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Ubicaciones Premium",
                description: "Espacios en las zonas más exclusivas y accesibles de la ciudad."
              },
              {
                title: "Flexibilidad Total",
                description: "Contratos adaptables a tus necesidades, desde horas hasta años."
              },
              {
                title: "Tecnología de Punta",
                description: "Infraestructura tecnológica de última generación incluida."
              },
              {
                title: "Soporte 24/7",
                description: "Equipo dedicado disponible para asistirte en todo momento."
              }
            ].map((item, index) => (
              <div
                key={index}
                className="text-center p-8 rounded-2xl bg-white shadow-lg"
              >
                <div className="w-12 h-12 rounded-full bg-sky-500 text-white flex items-center justify-center mx-auto mb-6 text-xl font-bold">
                  {index + 1}
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-600">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              Cómo Funciona
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Un proceso simple y transparente para encontrar tu espacio ideal.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Contáctanos", description: "Cuéntanos sobre tus necesidades y requerimientos." },
              { step: "02", title: "Visita", description: "Agenda una visita para conocer nuestros espacios." },
              { step: "03", title: "Propuesta", description: "Recibe una propuesta personalizada." },
              { step: "04", title: "Mudanza", description: "Te ayudamos con todo el proceso de instalación." }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="text-center">
                  <div className="text-6xl font-bold text-sky-100 mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-sm">
                    {item.description}
                  </p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-12 right-0 transform translate-x-1/2">
                    <ArrowRight className="w-6 h-6 text-slate-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-sky-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            ¿Necesitas un servicio personalizado?
          </h2>
          <p className="text-xl text-sky-100 mb-10 max-w-2xl mx-auto">
            Contáctanos y crearemos una solución a la medida de tu empresa.
          </p>
          <Link to="/contacto">
            <Button size="lg" className="bg-white text-sky-600 hover:bg-sky-50 px-8 py-6 text-lg shadow-xl">
              Solicitar Información
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
